import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  ApplicationSkeleton,
  RouteContentSkeleton,
} from '@/components/LoadingSkeleton';
import {
  defaultRoute,
  fallbackRoute,
  protectedRoutes,
  publicRoutes,
  rootRoute,
} from '@/config/app';
import type { AppRouteConfig } from '@/config/types';
import AppLayout from '@/layouts/AppLayout';
import {
  hasPlatformPermission,
  type PlatformPermission,
} from '@/services/business/permissions';
import { useAuthStore } from '@/stores/authStore';
import AdminGuard from './AdminGuard';
import AuthGuard from './AuthGuard';
import {
  InitialPageLoading,
  PageReady,
  useInitialPagePending,
} from './InitialPageLoading';

function LoadingBoundary({
  children,
  standalone,
}: {
  children: React.ReactNode;
  standalone: boolean;
}) {
  // First navigation suspends the whole layout; later navigations keep its shell.
  const initialPagePending = useInitialPagePending();
  if (initialPagePending) return children;
  return (
    <Suspense
      fallback={standalone ? <ApplicationSkeleton /> : <RouteContentSkeleton />}
    >
      {children}
    </Suspense>
  );
}

function PermissionGuard({
  children,
  permission,
}: {
  children: React.ReactNode;
  permission: PlatformPermission;
}) {
  const currentUser = useAuthStore((state) => state.currentUser);
  return hasPlatformPermission(currentUser, permission) ? (
    children
  ) : (
    <Navigate to="/exception/403" replace />
  );
}

function routeElement(route: AppRouteConfig, standalone: boolean) {
  const Page = route.component;
  const page = Page ? (
    <LoadingBoundary key={route.path} standalone={standalone}>
      <PageReady>
        <ErrorBoundary key={route.path} contained={!standalone}>
          <Page />
        </ErrorBoundary>
      </PageReady>
    </LoadingBoundary>
  ) : (
    <Outlet />
  );

  const accessControlledPage =
    route.access === 'canAdmin' ? <AdminGuard>{page}</AdminGuard> : page;
  return route.permission ? (
    <PermissionGuard permission={route.permission}>
      {accessControlledPage}
    </PermissionGuard>
  ) : (
    accessControlledPage
  );
}

function renderRoutes(
  routes: AppRouteConfig[],
  standalone = false,
  parentPath?: string,
): React.ReactNode {
  return routes.map((route) => {
    const isIndexRedirect = Boolean(route.redirect && route.path === parentPath);
    if (route.redirect) {
      return (
        <Route
          key={`${route.path}->${route.redirect}`}
          index={isIndexRedirect || undefined}
          path={isIndexRedirect ? undefined : route.path}
          element={<Navigate replace to={route.redirect} />}
        />
      );
    }

    if (route.index) {
      return (
        <Route
          key={`${parentPath}-index`}
          index
          element={routeElement(route, standalone)}
        />
      );
    }

    return (
      <Route
        key={route.path}
        path={route.path}
        element={routeElement(route, standalone)}
      >
        {route.routes ? renderRoutes(route.routes, standalone, route.path) : null}
      </Route>
    );
  });
}

export default function AppRouter() {
  return (
    <InitialPageLoading>
      <Routes>
        {renderRoutes(publicRoutes, true)}

        <Route element={<AuthGuard />}>
          <Route element={<AppLayout />}>{renderRoutes(protectedRoutes)}</Route>
        </Route>

        <Route
          path={rootRoute?.path ?? '/'}
          element={<Navigate replace to={rootRoute?.redirect ?? defaultRoute.path} />}
        />
        <Route
          path={fallbackRoute?.path ?? '*'}
          element={
            <Navigate replace to={fallbackRoute?.redirect ?? defaultRoute.path} />
          }
        />
      </Routes>
    </InitialPageLoading>
  );
}
